// THE COMPARISON'S PDF (busy-my-web#506, leftover of #488; frames 40006859:277503 and
// 40008216:411421).
//
// "Create PDF" on a finished comparison used to open a print window and call `window.print()`:
// the browser's own Save-as-PDF dialog, which the user has to drive, which they can cancel, and
// which leaves NOTHING behind. The frames draw the opposite — a chip, `Prada vs Louis Vuitton.pdf`,
// sitting under the "Comparison saved" step on the rail, next to the packs the comparison went
// into, on a story that has been reloaded since. A chip like that can only exist if the file was
// MADE and STORED, so that is what this module does:
//
//   1. render the comparison (its title, its markdown table, its recommendation, its sources) to a
//      real PDF **blob** in the browser — `buildComparisonPdf`;
//   2. put the bytes where every other attachment goes: `POST v1/document/upload/private`, then
//      `POST v1/document` to link them — `uploadComparisonPdf`, the same two calls SmartBox.vue's
//      `attachFilesToEntity` makes, in the same order, with the same provider;
//   3. hand back the record the chip is drawn from — `comparisonPdfChip`.
//
// WHY jsPDF. The repo had no PDF library at all (checked 2026-09-15: no jspdf / pdf-lib /
// html2pdf in package.json or node_modules). jsPDF is the one that answers the actual requirement
// here — `doc.output('blob')` hands back a real `Blob` with no DOM, no canvas and no network, so
// the bytes can be uploaded and the whole path can be unit-tested with the library mocked. The
// alternative the issue names, html2canvas, RASTERISES a DOM node: it needs the comparison to be
// on screen, it produces a picture of a table rather than a table, and it cannot run in jsdom. A
// comparison is text and a grid, which is exactly what jsPDF draws natively.
//
// It is loaded LAZILY (`await import('jspdf')`). The library is ~350Kb and "Create PDF" is a
// deliberate press on one card — no user who never touches it should pay for it in the main
// bundle. `buildComparisonPdf` also takes the constructor as an argument, which is how the tests
// hand it a stub without mocking the module system.

/** What the stored document — and its chip — is called. */
export const PDF_CONTENT_TYPE = 'application/pdf'

/** DocumentProviderEnum.GoogleDrive, the provider every private upload is linked under. */
export const PDF_PROVIDER = 2

/**
 * The file's name: the comparison's own title, made safe for a file system, plus `.pdf` —
 * "Prada vs Louis Vuitton.pdf" in frame 40006859:277503.
 *
 * @param {string} title the comparison title
 * @returns {string}
 */
export function comparisonPdfFilename(title) {
  const clean = String(title || '')
    // Only what a file system refuses. A HYPHEN is not one of those: "Ray-Ban sunglasses" is a
    // pack title, and turning it into "Ray Ban" would rename the user's own comparison.
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
  return `${clean || 'Comparison'}.pdf`
}

/**
 * Markdown emphasis, code ticks and links stripped back to the words themselves.
 *
 * `lib/packCompare.js` has a twin of this for HTML, where the answer is ESCAPED and the links are
 * kept as anchors. A PDF page has neither, so the text is flattened here rather than reaching for
 * that one and undoing it.
 */
export function plainText(s) {
  return String(s ?? '')
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '$1 ($2)')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .trim()
}

const isTableRow = (line) => /^\|.*\|$/.test(line)
const isTableSeparator = (line) => /^\|[\s:|-]+\|$/.test(line)
const rowCells = (line) => line.replace(/^\||\|$/g, '').split('|').map(c => plainText(c.trim()))

/**
 * The comparison's markdown as a list of things to DRAW — the same four shapes `comparisonHtml`
 * recognises (table, heading, bullet, paragraph), as data rather than as a string of tags, because
 * a PDF is laid out by measuring rather than by flowing.
 *
 * @param {string} md the AI's answer
 * @returns {Array<{type: string, text?: string, head?: string[], rows?: string[][]}>}
 */
export function comparisonBlocks(md) {
  const lines = String(md || '').split('\n')
  const blocks = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    if (isTableRow(line) && isTableSeparator((lines[i + 1] || '').trim())) {
      const head = rowCells(line)
      const rows = []
      let j = i + 2
      while (j < lines.length && isTableRow(lines[j].trim())) { rows.push(rowCells(lines[j].trim())); j++ }
      blocks.push({ type: 'table', head, rows })
      i = j - 1
      continue
    }
    if (/^#{1,6}\s+/.test(line)) { blocks.push({ type: 'heading', text: plainText(line.replace(/^#{1,6}\s+/, '')) }); continue }
    if (/^[-*•]\s+/.test(line)) { blocks.push({ type: 'bullet', text: plainText(line.replace(/^[-*•]\s+/, '')) }); continue }
    blocks.push({ type: 'para', text: plainText(line) })
  }
  return blocks
}

// A4 in points, the jsPDF default, with the page margins the frame's document wears.
const MARGIN = 40
const LINE = 13
const CELL_PAD = 5

/**
 * Render the comparison to a PDF blob.
 *
 * @param {object}   comparison        { title, answer, sources }
 * @param {object}   [opts]
 * @param {Function} [opts.jsPDF]      the constructor — injected by the tests; loaded lazily otherwise
 * @returns {Promise<Blob>} the PDF's bytes
 */
export async function buildComparisonPdf({ title, answer, sources } = {}, { jsPDF } = {}) {
  const Ctor = jsPDF || (await import('jspdf')).jsPDF
  const doc = new Ctor({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const width = pageWidth - MARGIN * 2
  const heading = String(title || 'Comparison')
  let y = MARGIN

  const room = (needed) => {
    if (y + needed <= pageHeight - MARGIN) return
    doc.addPage()
    y = MARGIN
  }
  const write = (text, { size = 10, style = 'normal', indent = 0, gap = 4 } = {}) => {
    doc.setFont('helvetica', style)
    doc.setFontSize(size)
    const lines = doc.splitTextToSize(text, width - indent)
    lines.forEach((l) => {
      room(LINE)
      doc.text(l, MARGIN + indent, y + size)
      y += LINE
    })
    y += gap
  }

  doc.setProperties?.({ title: heading, creator: 'Busy.me' })
  write(heading, { size: 18, style: 'bold', gap: 10 })

  comparisonBlocks(answer).forEach((b) => {
    if (b.type === 'table') { y = drawTable(doc, b, { y, width, pageHeight }); return }
    if (b.type === 'heading') { write(b.text, { size: 13, style: 'bold', gap: 6 }); return }
    if (b.type === 'bullet') { write(`•  ${b.text}`, { indent: 10 }); return }
    write(b.text)
  })

  const list = (sources || []).filter(s => s && (s.title || s.url))
  if (list.length) {
    write('Sources', { size: 13, style: 'bold', gap: 6 })
    list.forEach((s) => write(`•  ${plainText(s.title || s.url)}${s.url && s.title ? ` — ${s.url}` : ''}`, { size: 9, indent: 10, gap: 2 }))
  }

  return doc.output('blob')
}

/**
 * The comparison table, drawn as a grid: equal columns, each cell wrapped to its own width, the row as
 * tall as its tallest cell. A row that will not fit on what is left of the page starts a new one —
 * a comparison of five packs is several pages of table and none of it may be clipped.
 *
 * Exported so the geometry can be tested against a stub `doc` without a PDF ever being produced.
 */
export function drawTable(doc, { head = [], rows = [] }, { y, width, pageHeight }) {
  const cols = Math.max(head.length, ...rows.map(r => r.length), 1)
  const colWidth = width / cols
  let top = y + 4

  const drawRow = (cells, { bold = false } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(9)
    const wrapped = []
    for (let c = 0; c < cols; c++) wrapped.push(doc.splitTextToSize(String(cells[c] ?? ''), colWidth - CELL_PAD * 2))
    const height = Math.max(...wrapped.map(w => w.length)) * 11 + CELL_PAD * 2
    if (top + height > pageHeight - MARGIN) { doc.addPage(); top = MARGIN }
    for (let c = 0; c < cols; c++) {
      const x = MARGIN + c * colWidth
      doc.setDrawColor(229, 231, 235)
      doc.rect(x, top, colWidth, height)
      wrapped[c].forEach((l, i) => doc.text(l, x + CELL_PAD, top + CELL_PAD + 8 + i * 11))
    }
    top += height
  }

  if (head.length) drawRow(head, { bold: true })
  rows.forEach(r => drawRow(r))
  return top + 10
}

/**
 * Store the PDF the way an attachment is stored — the two calls `attachFilesToEntity` makes.
 *
 * The bytes go to the user's PRIVATE document area (never a shared one: a comparison is the user's
 * own research), and the record that LINKS them hangs off `timelineId`, which is what every reader
 * of `GET v1/document/{timelineId}/list` asks by. For a comparison that timeline key is the saved
 * comparison's own id — the shared research item every compared pack references — so the file is
 * found again by whoever is drawing that comparison, on any device, after any reload.
 *
 * @param {object} api          the general service (`uploadDocumentPrivate`, `addDocument`)
 * @param {object} args         { blob, filename, userId, timelineId }
 * @returns {Promise<object>}   { fileId, documentId, filename, contentType }
 * @throws when the upload answers without a fileId — a link to nothing is worse than a failure
 */
export async function uploadComparisonPdf(api, { blob, filename, userId, timelineId } = {}) {
  if (!blob || !timelineId) throw new Error('comparison pdf: nothing to upload')
  const form = new FormData()
  form.append('files', new File([blob], filename, { type: PDF_CONTENT_TYPE }), filename)
  const res = await api.uploadDocumentPrivate(form)
  const up = res?.data?.data?.result?.[0]
  if (!up?.fileId) throw new Error('comparison pdf: private upload returned no fileId')
  const name = up.filename || filename
  const link = await api.addDocument({
    provider: PDF_PROVIDER,
    userId,
    timelineId,
    // For a Drive file the "external entity" IS the file — both ids are the Drive fileId, exactly
    // as SmartBox's attach path writes them.
    externalEntityId: String(up.fileId),
    externalId: String(up.fileId),
    filename: name,
    contentType: up.contentType || PDF_CONTENT_TYPE,
    octets: blob.size || 0,
    hasThumbnail: false,
  })
  const documentId = link?.data?.data?.id || link?.data?.id || null
  return { fileId: up.fileId, documentId, filename: name, contentType: up.contentType || PDF_CONTENT_TYPE, timelineId }
}

/**
 * The chip the rail step and the receipt draw — `📕 Prada vs Louis Vuitton.pdf`, which opens the
 * document. Null when there is nothing stored, which is what keeps the chip off every comparison
 * that has never had a PDF made.
 *
 * `id` is what `GET v1/document/download/{id}` is asked for: the DOCUMENT record's id where the
 * link answered with one, and the Drive file id otherwise (the download route accepts both — the
 * document row is looked up by either).
 */
export function comparisonPdfChip(doc) {
  if (!doc) return null
  const id = doc.documentId || doc.id || doc.fileId || null
  if (!id) return null
  return {
    id,
    filename: doc.filename || 'Comparison.pdf',
    contentType: doc.contentType || PDF_CONTENT_TYPE,
    // The DRIVE file behind the record, kept beside the id the chip downloads by. It is what a
    // SECOND link needs: a PDF made before the comparison was filed is linked again under the
    // comparison once it has an item of its own (the comparison store's `save`), and that link
    // addresses the file, not the document row.
    ...(doc.fileId ? { fileId: doc.fileId } : {}),
  }
}

/**
 * The first PDF among the documents stored against a comparison (`GET v1/document/{id}/list`), as
 * a chip. A comparison has at most one — "Create PDF" replaces the record it holds — but the list
 * belongs to a timeline key, so it is read defensively.
 */
export function pdfFromDocuments(list) {
  const rows = Array.isArray(list) ? list : []
  const pdf = rows.find(d => String(d?.contentType || '').includes('pdf')
    || /\.pdf$/i.test(String(d?.filename || d?.name || '')))
  if (!pdf) return null
  return comparisonPdfChip({ documentId: pdf.id, filename: pdf.filename || pdf.name, contentType: pdf.contentType })
}
